import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents } from '@auctisync/shared';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  confirmPreauth,
  describePreauthStatus,
  fetchExistingPreauth,
  startPreauthSetup,
} from '@auctisync/api-client';
import { supabase } from '../lib/supabase';
import { loadStripeForAccount, missingStripeKeyMessage, stripeConfigured } from '../lib/stripe';

/**
 * CcPreauthPanel — shown on the bidder's lot page when:
 *   - auction.requires_cc_preauth is true
 *   - the bidder is approved on the auction
 *   - no active (status='authorized') cc_preauth row exists yet
 *
 * The panel explains the hold, lets the bidder save a card via Stripe
 * Elements (SetupIntent flow on the tenant's Connect account), and
 * then creates the actual manual-capture PaymentIntent server-side via
 * create-cc-preauth. On success, parent is notified so the bid form
 * can take over.
 *
 * If a hold already exists we show a summary so the bidder can see
 * "card on file for $X" and the expiry.
 */
export function CcPreauthPanel({
  auctionId,
  userId,
  onAuthorized,
}: {
  auctionId: string;
  userId: string;
  onAuthorized: () => void;
}) {
  const [existing, setExisting] = useState<{
    id: string;
    amount_cents: number;
    expires_at: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase machine: summary (we already have an active hold) | invite | elements | confirming
  const [phase, setPhase] = useState<'invite' | 'elements' | 'confirming'>('invite');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [preauthAmountCents, setPreauthAmountCents] = useState<number | null>(null);

  // The three server calls live in @auctisync/api-client so the app runs the
  // identical flow. Card COLLECTION differs (Elements here, PaymentSheet on
  // native) and is the only step that legitimately does.
  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await fetchExistingPreauth(supabase, { auctionId, userId });
    if (!result.ok) {
      setError(result.message);
    } else {
      setExisting(
        result.preauth
          ? {
              id: result.preauth.id,
              amount_cents: result.preauth.amountCents,
              expires_at: result.preauth.expiresAt,
            }
          : null,
      );
    }
    setLoading(false);
  }, [auctionId, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const beginSetup = useCallback(async () => {
    if (!stripeConfigured()) {
      setError(missingStripeKeyMessage());
      return;
    }
    setError(null);
    setPhase('confirming');
    try {
      const result = await startPreauthSetup(supabase, { auctionId });
      if (!result.ok) {
        setError(result.message);
        setPhase('invite');
        return;
      }
      setClientSecret(result.setup.clientSecret);
      setStripeAccountId(result.setup.stripeAccountId);
      setPreauthAmountCents(result.setup.preauthAmountCents);
      setPhase('elements');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('invite');
    }
  }, [auctionId]);

  if (loading) {
    return <div style={panelStyle}>Checking your card on file…</div>;
  }

  if (existing) {
    return (
      <div style={panelStyle}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge variant="success">Card on file</Badge>
          <span style={{ color: 'var(--text)' }}>
            You're held for <strong>{formatCents(existing.amount_cents)}</strong>
            {existing.expires_at && (
              <>
                {' '}
                until <strong>{new Date(existing.expires_at).toLocaleDateString()}</strong>
              </>
            )}
            .
          </span>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '0.5rem 0 0' }}>
          Your card isn't charged unless you win — it's just a temporary hold so the auction house
          can verify your funding.{' '}
          <LearnMoreLink helpSlug="what-pre-authorization-means" newTab>
            What pre-authorization means
          </LearnMoreLink>
        </p>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge variant="outline">Card required</Badge>
        <span style={{ color: 'var(--text)' }}>
          You're approved, but this auction requires a card on file before bidding.
        </span>
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {phase === 'invite' && (
        <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
          <p style={{ fontSize: '0.9rem', color: 'var(--text)', margin: 0 }}>
            The auction house will place a temporary hold on your card. The hold is released when
            the auction closes — you're only charged if you win.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => {
                void beginSetup();
              }}
              type="button"
            >
              Add a card to bid
            </Button>
            <LearnMoreLink helpSlug="what-pre-authorization-means" newTab>
              How holds work
            </LearnMoreLink>
          </div>
        </div>
      )}

      {phase === 'confirming' && (
        <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
          Opening secure card entry…
        </div>
      )}

      {phase === 'elements' && clientSecret && stripeAccountId && (
        <SetupPanel
          clientSecret={clientSecret}
          stripeAccountId={stripeAccountId}
          auctionId={auctionId}
          amountLabel={preauthAmountCents ? formatCents(preauthAmountCents) : ''}
          onCancel={() => {
            setPhase('invite');
            setClientSecret(null);
            setStripeAccountId(null);
          }}
          onAuthorized={() => {
            setPhase('invite');
            setClientSecret(null);
            setStripeAccountId(null);
            void refresh();
            onAuthorized();
          }}
        />
      )}
    </div>
  );
}

function SetupPanel({
  clientSecret,
  stripeAccountId,
  auctionId,
  amountLabel,
  onAuthorized,
  onCancel,
}: {
  clientSecret: string;
  stripeAccountId: string;
  auctionId: string;
  amountLabel: string;
  onAuthorized: () => void;
  onCancel: () => void;
}) {
  const stripePromise = useMemo(() => loadStripeForAccount(stripeAccountId), [stripeAccountId]);
  return (
    <div style={{ marginTop: '0.75rem' }}>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 0 }}>
        Enter your card. {amountLabel && `You'll be held for ${amountLabel}.`}
      </p>
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
        <SetupForm auctionId={auctionId} onAuthorized={onAuthorized} onCancel={onCancel} />
      </Elements>
    </div>
  );
}

function SetupForm({
  auctionId,
  onAuthorized,
  onCancel,
}: {
  auctionId: string;
  onAuthorized: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErr(null);

    const setupRes = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (setupRes.error) {
      setErr(setupRes.error.message ?? 'Card setup failed.');
      setBusy(false);
      return;
    }

    const si = setupRes.setupIntent;
    const pm = typeof si?.payment_method === 'string' ? si.payment_method : si?.payment_method?.id;
    if (!si || !pm) {
      setErr('Card saved but the payment method could not be resolved. Please try again.');
      setBusy(false);
      return;
    }

    // setup_intent_id links the hold back to the card-consent event
    // (persisted as cc_preauths.stripe_setup_intent_id, audit trail).
    const result = await confirmPreauth(supabase, {
      auctionId,
      paymentMethodId: pm,
      setupIntentId: si.id,
    });
    if (!result.ok) {
      setErr(result.message);
      setBusy(false);
      return;
    }

    // ⚠️ This branch used to be missing. Any response without an `error` field
    // was treated as success and `onAuthorized()` was called — so a hold coming
    // back `requires_action` (the bank wants a challenge) or `failed` (the card
    // was refused) told the bidder they were clear to bid when no hold existed.
    // They would then be refused by place_bid with no idea why.
    if (result.status !== 'authorized') {
      setErr(describePreauthStatus(result.status).text);
      setBusy(false);
      return;
    }

    setBusy(false);
    onAuthorized();
  }

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      style={{ display: 'grid', gap: '0.75rem' }}
    >
      <PaymentElement />
      {/* Card-on-file authorization — the pre-auth flow places the hold (and,
          on a win, the invoice charge) off-session, so the mandate disclosure
          must sit with the save action. The house name isn't available on the
          public lot page, so we use the generic form. */}
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
        I authorize the auction house to place this temporary hold and to charge this card for
        invoices on lots I win at their auctions.
      </p>
      {err && (
        <div role="alert" style={errorStyle}>
          {err}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy || !stripe}>
          {busy ? 'Saving…' : 'Save card & place hold'}
        </Button>
      </div>
    </form>
  );
}

const panelStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  borderRadius: '0.5rem',
  background: 'var(--surface-muted, #fef3c7)',
  border: '1px solid var(--border)',
  marginBottom: '1rem',
  display: 'grid',
  gap: '0.25rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: '0.375rem',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  fontSize: '0.85rem',
};
